import React from 'react';
import { Puzzle, Settings, PlayCircle, Check, Clock, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { AgentData_V10, AgentHubPageViewMode, HowItWorksStepV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { VisualElement } from '@dopomogai/ui'; // Assuming this component exists and works
import {  
  INNER_CARD_ANIMATE_RESTING,
  INNER_CARD_HOVER,
  INNER_CARD_TRANSITION_HOVER,
} from '../../shared/animationConstants';

interface SetupSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

// Helper to get an icon for a step if not provided by name
const getDefaultStepIcon = (stepNumber: number): React.ReactNode => {
  // Can be expanded to map step.iconName to a specific Lucide icon
  switch (stepNumber % 3) {
    case 1: return <Puzzle size={28} className="text-amber-700" />;
    case 2: return <Settings size={28} className="text-amber-700" />;
    case 0: return <PlayCircle size={28} className="text-amber-700" />;
    default: return <Check size={28} className="text-amber-700" />;
  }
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      staggerChildren: 0.15, // Stagger animation for children
    },
  },
};

const stepCardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

export const SetupSection = React.forwardRef<HTMLDivElement, SetupSectionProps>(
  ({ agentData, viewMode, onAction, headerBgColor, id }, ref) => {
  const { t } = useTranslation('agent-common');
  const isOwnerView = viewMode === 'owner_dashboard';
  const { setupSectionPublic } = agentData;

  if (!setupSectionPublic || !setupSectionPublic.steps || setupSectionPublic.steps.length === 0) {
    if (isOwnerView) {
      return (
        <Card id={id} ref={ref} titleKey="setupSection.defaultTitle" headerBgColor={headerBgColor}>
          <p className="text-gray-500 italic p-4 text-center">{t('setupSection.owner.noStepsDefined')}</p>
          <div className="p-4 text-center">
            <Button
              textKey="setupSection.owner.defineStepsButton"
              variant="outline"
              size="sm"
              onClick={() => onAction('EDIT_SETUP_STEPS', null)}
            />
          </div>
        </Card>
      );
    }
    return null;
  }

  return (
    <Card
      id={id}
      ref={ref}
      title={setupSectionPublic.title || t('setupSection.defaultTitle')}
      icon={<Settings size={22} className="text-amber-600" />}
      headerBgColor={headerBgColor}
      noPadding // We will control padding inside
      actionButton={
        isOwnerView && (
          <Button
            textKey="setupSection.owner.editStepsButton"
            variant="outline"
            size="sm"
            icon={<Settings size={14} />}
            onClick={() => onAction('EDIT_SETUP_STEPS', null)}
          />
        )
      }
    >
      <div className="p-6 md:p-8">
        {setupSectionPublic.typicalCoreSetupTime && (
          // ENHANCED Setup Time Spotlight
          <div className="mb-8 md:mb-12 text-center">
            <div className="inline-flex flex-col items-center bg-amber-50 p-4 rounded-xl border-2 border-black shadow-sm">
              <div className="flex items-center gap-2 text-amber-800">
                <Clock size={18} />
                <p className="text-sm font-medium">{t('setupSection.typicalSetupTimeLabel')}</p>
              </div>
              <p className="font-bold text-2xl md:text-3xl text-amber-900 mt-1">{setupSectionPublic.typicalCoreSetupTime}</p>
            </div>
          </div>
        )}

        {/* ENHANCED Horizontal Scrolling Timeline */}
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="flex items-stretch gap-4 md:gap-6 -m-3 p-3 overflow-x-auto overflow-y-hidden scrollbar-hide"
        >
          {setupSectionPublic.steps.map((step: HowItWorksStepV10, index: number) => (
            <React.Fragment key={index}>
              {/* Step Card */}
              <motion.div
                variants={stepCardVariants}
                animate={INNER_CARD_ANIMATE_RESTING}
                whileHover={INNER_CARD_HOVER}
                transition={INNER_CARD_TRANSITION_HOVER}
                className="flex-shrink-0 w-[280px] sm:w-[320px] bg-white rounded-xl border-2 border-black flex flex-col overflow-hidden"
              >
                {step.media && (
                  <div className="border-b-2 border-black aspect-video bg-slate-100">
                    <VisualElement visual={step.media} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5 flex flex-col items-start flex-grow">
                  <div className="flex items-center gap-3 w-full mb-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-800 font-bold text-lg border-2 border-black shadow-sm">
                      {step.stepNumber || index + 1}
                    </div>
                    <h3 className="text-md font-semibold text-gray-800 flex-1">{step.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow">{step.description}</p>
                </div>
              </motion.div>

              {/* Connector Arrow (Desktop only) */}
              {index < setupSectionPublic.steps.length - 1 && (
                <div className="hidden md:flex items-center justify-center flex-shrink-0">
                  <ChevronRight size={32} className="text-slate-300" />
                </div>
              )}
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </Card>
  );
});

SetupSection.displayName = "SetupSection";
